"use client";

import type { ReactNode } from "react";
import type { SubjectWithScopeId } from "@/hooks/useAbility";
import { useAbility } from "@/hooks/useAbility";
import type { Action, Subject } from "@/lib/ability/types";

export interface CanProps {
  action: Action;
  resource: Subject | SubjectWithScopeId;
  scopeId?: string;
  field?: string;
  children: ReactNode;
  fallback?: ReactNode;
  not?: boolean;
  loadingFallback?: ReactNode;
}

export function Can({
  action,
  resource,
  scopeId,
  field,
  children,
  fallback = null,
  not = false,
  loadingFallback = null,
}: CanProps) {
  const { can, isLoading, error } = useAbility();

  if (isLoading) {
    return loadingFallback;
  }

  if (error) {
    return fallback;
  }

  const normalizedSubject =
    typeof resource === "string"
      ? { scopeType: resource, scopeId }
      : scopeId !== undefined
        ? { ...resource, scopeId }
        : resource;
  const hasPermission = can(action, normalizedSubject as SubjectWithScopeId, field);
  const shouldShow = not ? !hasPermission : hasPermission;

  return shouldShow ? children : fallback;
}
